import React from 'react';
import styled from 'styled-components';
import {
  FileUpload,
  FileUploadGroup,
  FileUploadGroupType,
} from '../lib/file-upload';
import {
  token,
  Text,
  TextButton,
  IconWarning,
  IconPlus,
} from '@zillow/constellation';
import FileItem from './FileItem';
import {
  DropZoneOverlay,
  DropZoneRendererProps,
  OnAddFilesClickType,
} from './DropZone';
import { FileGroupsProps } from './FileManager';

const Header = styled.div`
  display: grid;
  grid-template-columns: 5fr minmax(auto, 1fr);
  padding-bottom: 0.75rem;
  min-height: 2.5rem;
`;

const Description = styled.div`
  white-space: nowrap;
`;

const StyledIconWarning = styled(IconWarning)`
  margin-right: 0.25rem;
`;

const Action = styled.div`
  text-align: right;
  white-space: nowrap;
`;

const Files = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210.5px, 1fr));
  justify-items: center;
  align-items: start;
  grid-gap: 1rem;

  @media (max-width: 500px) {
    row-gap: 0.5rem;
  }
`;

export type FileItemGroupConsumableProps = {
  acceptDescription?: string;
  onDelete: (file: FileUpload) => void;
  canAddFiles: boolean;
  onAddFiles: OnAddFilesClickType;
  addFilesDescription: string;
};

export type FileItemGroupProps = {
  fileGroup: FileUploadGroup;
  className?: string;
} & FileItemGroupConsumableProps;

export function FileItemGroup({
  fileGroup,
  acceptDescription,
  canAddFiles,
  onAddFiles,
  addFilesDescription,
  onDelete,
  className,
}: FileItemGroupProps) {
  const isError =
    fileGroup.groupType === FileUploadGroupType.Rejected ||
    fileGroup.groupType === FileUploadGroupType.Failed;

  return (
    <div className={className}>
      <Header>
        <Description>
          {isError && <StyledIconWarning size="sm" fontColor="orange500" />}
          <Text fontType="bodyHeading">{fileGroup.description}</Text>
        </Description>
        <Action>
          {canAddFiles && (
            <TextButton
              fontType="bodyHeading"
              onClick={onAddFiles}
              icon={<IconPlus />}
            >
              {addFilesDescription}
            </TextButton>
          )}
        </Action>
      </Header>
      <Files>
        {fileGroup.groupFiles.map(fileUpload => (
          <FileItem
            key={fileUpload.id}
            fileUpload={fileUpload}
            acceptDescription={acceptDescription}
            onDelete={onDelete}
          />
        ))}
      </Files>
    </div>
  );
}

const Container = styled.div<{ isDragging: boolean }>`
  outline: 1px solid;
  outline-color: ${props =>
    props.isDragging ? token('colors.blue400') : 'transparent'};
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
`;

export type RenderFilesConsumableProps = DropZoneRendererProps &
  FileGroupsProps &
  FileItemGroupConsumableProps;

export type Props = {
  className?: string;
} & RenderFilesConsumableProps;

export default function FileItemGroups({
  isDragOutside,
  isDragInside,
  fileGroups,
  acceptDescription,
  canAddFiles,
  onAddFiles,
  addFilesDescription,
  onDelete,
  className,
}: Props) {
  return (
    <Container className={className} isDragging={isDragOutside || isDragInside}>
      {isDragInside && <DropZoneOverlay />}
      {fileGroups.map((fileGroup, groupIndex) => (
        <FileItemGroup
          key={fileGroup.groupType}
          fileGroup={fileGroup}
          acceptDescription={acceptDescription}
          canAddFiles={canAddFiles && groupIndex === 0}
          onAddFiles={onAddFiles}
          addFilesDescription={addFilesDescription}
          onDelete={onDelete}
        />
      ))}
    </Container>
  );
}
